import React, { useEffect, useRef, useCallback } from 'react';

import { MediaItem } from '../App';

interface GalleryProps {
  mediaItems: MediaItem[];
  onMediaSelect: (media: MediaItem) => void;
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

const Gallery: React.FC<GalleryProps> = ({ 
  mediaItems, 
  onMediaSelect, 
  isLoading = false, 
  hasMore = false,
  onLoadMore 
}) => {
  const loaderRef = useRef<HTMLDivElement>(null);

  // Handle intersection observer for infinite scrolling
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !isLoading && onLoadMore) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);

  // Set up intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "20px",
      threshold: 0.1
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [handleObserver]);

  if (mediaItems.length === 0 && !isLoading) {
    return (
      <div className="text-center py-12 px-4 bg-gray-50 rounded-lg">
        <p className="text-gray-600">No media items found. Upload some using the form above!</p>
      </div>
    );
  }

  return (
    <div className="mt-4 sm:mt-8">
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
        {mediaItems.map((media) => (
          <div 
            key={media.id} 
            className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer transform hover:-translate-y-1 transition-transform duration-300"
            onClick={() => onMediaSelect(media)}
          >
            {media.content_type === 'video' ? (
              <div className="relative h-36 sm:h-48 bg-gray-100">
                <video 
                  src={media.url + '#t=0.001'}
                  className="w-full h-full object-cover" 
                  onError={(e) => console.error("Video error:", e)}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white bg-opacity-80 text-blue-500">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-36 sm:h-48 bg-gray-100">
                <img 
                  src={media.thumbnailUrl || media.url}
                  alt={media.name} 
                  className="w-full h-full object-cover" 
                  loading="lazy"
                  onError={(e) => {
                    console.error("Image error:", e);
                    // Fallback to placeholder on error
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Image+Error';
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Loading indicator and observer reference */}
      <div ref={loaderRef} className="w-full text-center py-8">
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-t-blue-500 border-r-transparent border-b-blue-500 border-l-transparent rounded-full animate-spin"></div>
            <span className="ml-2 text-gray-600">Loading more...</span>
          </div>
        ) : hasMore ? (
          <div className="h-10"></div> // Empty space for the observer to detect
        ) : mediaItems.length > 0 ? (
          <p className="text-gray-500 text-sm">You have reached the end of the gallery</p>
        ) : null}
      </div>

      {/* Stats display showing total loaded items */}
      <div className="text-center text-sm text-gray-500 mt-4">
        Showing {mediaItems.length} items {hasMore ? '(scroll for more)' : ''}
      </div>
    </div>
  );
};

export default Gallery;